import { useState, useEffect } from 'react';
import { supabase } from '../providers/supabase/supabase';
import type { SelectedRecommendation } from '../types';

export interface UseRecommendationSelectionsResult {
    selections: SelectedRecommendation | null;
    loading: boolean;
    error: string | null;
    updateSelections: (selections: Partial<SelectedRecommendation>) => Promise<void>;
    saving: boolean;
}

export const useRecommendationSelections = (
    artistId?: string | null, 
    contactId?: string | null
): UseRecommendationSelectionsResult => {
    const [selections, setSelections] = useState<SelectedRecommendation | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!artistId || !contactId) {
            setSelections(null);
            setLoading(false);
            setError(null);
            return;
        }

        const fetchSelections = async () => {
            try {
                setLoading(true);
                setError(null);

                console.log('🎯 Fetching recommendation selections for:', { artistId, contactId });

                const { data, error: fetchError } = await supabase
                    .from('selected_recommendations')
                    .select('*')
                    .eq('artist_id', artistId)
                    .eq('contact_id', contactId)
                    .single();

                if (fetchError) {
                    if (fetchError.code === 'PGRST116') {
                        // No selections found - create default empty selections
                        const defaultSelections: SelectedRecommendation = {
                            artist_id: artistId,
                            contact_id: contactId,
                            selected_similar_tracks: [],
                            selected_editorial_playlists: [],
                            selected_user_playlists: [],
                        };
                        setSelections(defaultSelections);
                        setError(null);
                    } else {
                        throw fetchError;
                    }
                } else {
                    setSelections(data);
                }
            } catch (err) {
                console.error('Error fetching recommendation selections:', err);
                setError(err instanceof Error ? err.message : 'Failed to fetch selections');
            } finally {
                setLoading(false);
            }
        };

        fetchSelections();
    }, [artistId, contactId]);

    const updateSelections = async (updates: Partial<SelectedRecommendation>) => {
        if (!artistId || !contactId) {
            throw new Error('Artist ID and Contact ID are required');
        }

        try {
            setSaving(true);
            setError(null);

            const updatedSelections = {
                artist_id: artistId,
                contact_id: contactId,
                selected_similar_tracks: updates.selected_similar_tracks || selections?.selected_similar_tracks || [],
                selected_editorial_playlists: updates.selected_editorial_playlists || selections?.selected_editorial_playlists || [],
                selected_user_playlists: updates.selected_user_playlists || selections?.selected_user_playlists || [],
                updated_at: new Date().toISOString(),
            };

            console.log('💾 Saving recommendation selections:', updatedSelections);

            const { data, error: saveError } = await supabase
                .from('selected_recommendations')
                .upsert(updatedSelections, { onConflict: 'artist_id,contact_id' })
                .select()
                .single();

            if (saveError) throw saveError;

            setSelections(data);
            console.log('✅ Selections saved successfully');
            
            // Trigger email data refresh by dispatching a custom event
            console.log('🔥 DISPATCHING EVENT to refresh email data:', { artistId, contactId });
            window.dispatchEvent(new CustomEvent('recommendationSelectionChanged', {
                detail: { artistId, contactId, selections: data }
            }));
            
        } catch (err) {
            console.error('Error saving recommendation selections:', err);
            setError(err instanceof Error ? err.message : 'Failed to save selections');
            throw err;
        } finally {
            setSaving(false);
        }
    };

    return { 
        selections, 
        loading, 
        error, 
        updateSelections, 
        saving 
    };
};